import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core';
// Tree-shaken ECharts: import only the chart types + components we use, keeping
// the bundle small (the full `echarts` barrel is ~900 kB).
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { ThemeService } from '../../core/theme/theme.service';

echarts.use([
  LineChart,
  BarChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  GraphicComponent,
  CanvasRenderer,
]);

/**
 * Thin standalone ECharts wrapper.
 *
 * Pass an ECharts `option` object; the component renders it into a sized host,
 * re-applies on change (signal-driven), re-themes when the global dark-mode
 * toggle flips, and resizes with the viewport. Colours in the option should use
 * the CapX chart palette (see `chartPalette()` / `axisTheme()` helpers) so charts
 * stay on-system in both themes.
 */
@Component({
  selector: 'app-chart',
  template: `<div #host class="chart-host" [style.height]="height()"></div>`,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
      .chart-host {
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent implements AfterViewInit, OnDestroy {
  private readonly theme = inject(ThemeService);
  private readonly hostRef = viewChild.required<ElementRef<HTMLDivElement>>('host');

  /** ECharts option object. */
  readonly option = input.required<echarts.EChartsCoreOption>();
  /** CSS height for the chart host (default 280px). */
  readonly height = input<string>('280px');

  private chart: echarts.ECharts | null = null;
  private readonly resize = () => this.chart?.resize();

  constructor() {
    // Re-render whenever the option or the theme changes.
    effect(() => {
      const opt = this.option();
      // Touch the theme signal so the effect re-runs on toggle.
      this.theme.isDark();
      this.applyOption(opt);
    });
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.resize);
    // The constructor effect has already run; render once the host exists.
    this.applyOption(this.option());
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resize);
    this.chart?.dispose();
    this.chart = null;
  }

  private applyOption(option: echarts.EChartsCoreOption): void {
    const host = this.hostRef().nativeElement;
    // Dispose + recreate on theme change so the base theme is correct.
    if (this.chart) {
      this.chart.dispose();
    }
    this.chart = echarts.init(host, undefined, { renderer: 'canvas' });
    this.chart.setOption(option, true);
    this.chart.resize();
  }
}

/** Brand-leaning categorical palette that reads in both light and dark mode. */
export function chartPalette(): string[] {
  return [
    '#3b82f6', // blue   (primary)
    '#10b981', // green  (success/approve)
    '#f59e0b', // amber  (warning/more-info)
    '#ef4444', // red    (destructive/decline)
    '#8b5cf6', // violet
    '#06b6d4', // cyan
    '#ec4899', // pink
    '#64748b', // slate
  ];
}

/** Axis / grid / tooltip colours for the current theme. */
export function axisTheme(isDark: boolean) {
  const axis = isDark ? '#94a3b8' : '#64748b';
  const split = isDark ? 'rgba(148,163,184,0.15)' : 'rgba(100,116,139,0.15)';
  const tooltipBg = isDark ? '#1e293b' : '#ffffff';
  const tooltipText = isDark ? '#e2e8f0' : '#1e293b';
  return { axis, split, tooltipBg, tooltipText };
}
